import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, InputAdornment, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { FieldSwitchButton } from '../components/FieldSwitchButton';
import { IndividualNameSummary, IndividualSelector } from '../../selectors/IndividualSelector';
import { TranslationTextList } from '../../../locales/common';
import { TitleAutocompleteSelector } from '../../selectors/TitleAutocompleteSelector';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherContactDetailsPanel');

// Explicit request (EnquiryFormDrawer split): the client's "Contact Details" accordion (title, name, phone, email ...).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherContactDetailsPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    onDropDownSelect: (form: FormApi<Enquiry>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    onGetPartyAddresses: (form: FormApi<Enquiry>, values: AnyObject, selection: IndividualNameSummary, name: string) => void;
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    translate: (key: keyof TranslationTextList) => string;
    onContactDetailsChanged: (form: FormApi<any>, values: AnyObject) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    isSourceTypeClientOnly: true | undefined;
}

interface OtherContactDetailsPanelBodyProps extends OtherContactDetailsPanelProps {
    formValues: AnyObject;
}

const OtherContactDetailsPanelBody: React.FC<OtherContactDetailsPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, onDropDownSelect, onGetPartyAddresses, onSwitch, translate, onContactDetailsChanged, onExpandChange, isSourceTypeClientOnly,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-contact-details-content"
                id="other-contact-details-header"
                className={classes.expansionPanelSummary}
            >
                <Typography className={classes.heading}>
                    <PersonAddIcon className={`${classes.headingIcon} ${classes.headingIconContact}`} />
                    Client Contact Details - where different to the Enquirer
                </Typography>
                {/* <div className={classes.infoContainer}>
                    <div className="col">
                        {panelTitle}
                    </div>
                </div> */}
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientTitle"
                            // validate={required} 
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <>
                                    <TitleAutocompleteSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Title"
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        value={input.value ? { label: input.value, value: input.value } : undefined}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {
                                            input.onChange(selection ? selection.value : '');
                                        }}
                                        // required={true}
                                        helperText={meta.error && meta.touched ? 'Title is required' : ''}
                                    />
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientFirstName"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Client Given Name(s)"
                                    multiline={true}
                                    // required={true}
                                    disabled={values.clientGuid}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Client Given Name(s) is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>

                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.toggleFieldWrapepr}>
                            <div className="field">
                                {
                                    values.isClientNameSwitched ? (
                                        <Field
                                            name="clientIndividualSelector"
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <IndividualSelector
                                                    variant="outlined"
                                                    size="small"
                                                    {...input}
                                                    key="clientIndividualSelector"
                                                    label="Individual Name"
                                                    value={values.clientIndividualSelector}
                                                    error={meta.error && meta.touched}
                                                    helperText={
                                                        meta.error &&
                                                            meta.touched
                                                            ? 'Individual Name is required'
                                                            : ''
                                                    }
                                                    onSelection={(selection: IndividualNameSummary, name: string) => {
                                                        const values = form.getState().values;
                                                        input.onChange(selection);
                                                        onDropDownSelect(form, values, input, selection, 'clientIndividualSelector');
                                                        onGetPartyAddresses(form, values, selection, 'clientIndividualSelector');
                                                    }}
                                                    enableSecondary={true}
                                                    includeOrganisation={false}
                                                    isSourceTypeClientOnly={isSourceTypeClientOnly}
                                                    endAdornmentExtra={(
                                                        <FieldSwitchButton
                                                            tooltip="Switch to enter the last name"
                                                            onClick={() => onSwitch(form, form.getState().values, 'clientLastName')}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Field>
                                    ) :
                                        (
                                            <Field
                                                name="clientLastName"
                                                // validate={!isTabActivity() ? required : undefined}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...input}
                                                        label="Client Surname"
                                                        type="text"
                                                        // required={!isTabActivity()}
                                                        className={classes.textField}
                                                        error={
                                                            meta.error &&
                                                            meta.touched
                                                        }
                                                        disabled={values.clientGuid}
                                                        autoComplete="abcd"
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Client Surname is required'
                                                                : ''
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FieldSwitchButton
                                                                        tooltip="Switch to select an existing client & contact"
                                                                        onClick={() => onSwitch(form, form.getState().values, 'clientLastName')}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )
                                }
                            </div>
                        </div>
                    </Grid>

                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientRelationshipToEnquirer"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label={translate('Relationship to the enquirer')}
                                    multiline={true}
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? `${translate('Relationship to the enquirer')} is required`
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientEmail"
                            type="email"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Email"
                                        type="email"
                                        // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                        className={classes.textField}
                                        // tslint:disable-next-line: no-any
                                        onChange={(event: any) => {
                                            const values = form.getState().values;
                                            input.onChange(event.target.value);
                                            onContactDetailsChanged(form, values);
                                        }}
                                        error={
                                            meta.error &&
                                            meta.touched
                                        }
                                        autoComplete="abcd"
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? meta.error
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientPhone"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Telephone"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Phone is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="clientMobile"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Mobile"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Mobile is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherContactDetailsPanelBody.displayName = 'OtherContactDetailsPanelBody';

export const OtherContactDetailsPanel: React.FC<OtherContactDetailsPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherContactDetailsPanelBody
                {...panelProps}
                formValues={{
                    clientGuid: values.clientGuid,
                    isClientNameSwitched: values.isClientNameSwitched,
                    clientIndividualSelector: values.clientIndividualSelector,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherContactDetailsPanel.displayName = 'OtherContactDetailsPanel';
